import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/use-subscription';
import { useSubscriptionUsage } from '@/hooks/use-subscription-usage';
import { Crown, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface TierBadgeProps {
  isOpen: boolean;
  className?: string;
}

const FREE_APPLICATION_LIMIT = 5;

function TierBadgeComponent({ isOpen, className }: TierBadgeProps) {
  const { user } = useAuth();
  const { userTier, isLoading, isPro } = useSubscription(user?.id);
  const location = useLocation();
  const navigate = useNavigate();

  // Determine user role from path
  const isFilmmaker = location.pathname.includes('/filmmaker/');
  const userRole = isFilmmaker ? 'filmmaker' : 'client';

  const { data: usage, isLoading: isLoadingUsage } = useSubscriptionUsage(user?.id, userRole);

  const handleUpgradeClick = () => {
    const subscribePath = isFilmmaker
      ? '/filmmaker/dashboard/subscribe'
      : '/client/dashboard/subscribe';
    navigate(subscribePath);
  };

  if (isLoading) {
    return null;
  }

  const applicationsUsed = usage?.applications || 0;
  const isNearLimit = applicationsUsed >= FREE_APPLICATION_LIMIT * 0.8;
  const isAtLimit = applicationsUsed >= FREE_APPLICATION_LIMIT;

  if (!isOpen) {
    // Icon only version - centered like other nav icons
    // Show warning color if near/at limit for free users
    const showWarning = !isPro && isFilmmaker && isNearLimit;

    return (
      <div
        onClick={!isPro ? handleUpgradeClick : undefined}
        className={cn(
          'flex items-center justify-center h-10 w-10 rounded-md transition-all',
          isPro
            ? 'bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 dark:from-yellow-400/30 dark:to-yellow-600/30'
            : showWarning
              ? 'bg-orange-500/20 dark:bg-orange-500/30 cursor-pointer hover:bg-orange-500/30'
              : 'bg-primary/10 dark:bg-primary/20 cursor-pointer hover:bg-primary/20',
          className
        )}
        title={isPro ? 'FMF Pro' : `Free - ${applicationsUsed}/${FREE_APPLICATION_LIMIT} applications`}
      >
        <Crown className={cn(
          "h-5 w-5",
          isPro
            ? "text-yellow-600 dark:text-yellow-400"
            : showWarning
              ? "text-orange-600 dark:text-orange-400"
              : "text-primary"
        )} />
      </div>
    );
  }

  // Full version for Pro users
  if (isPro) {
    return (
      <div
        className={cn(
          'flex items-center justify-center gap-3 px-4 py-2.5 rounded-full',
          'bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 dark:from-yellow-400/30 dark:to-yellow-600/30 ring-1 ring-yellow-500/30',
          className
        )}
      >
        <Crown className="h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-400" />
        <span className="text-sm font-medium whitespace-nowrap text-yellow-700 dark:text-yellow-300">
          FMF Pro
        </span>
      </div>
    );
  }

  // Full version for Free users - show usage + upgrade CTA
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Usage indicator for filmmakers */}
      {isFilmmaker && (
        <div
          className={cn(
            'flex items-center justify-between px-3 py-2 rounded-lg text-xs',
            isNearLimit
              ? 'bg-orange-500/10 dark:bg-orange-500/20'
              : 'bg-primary/5 dark:bg-primary/10'
          )}
        >
          <span className="text-muted-foreground font-medium">Applications</span>
          <span className={cn(
            'font-bold',
            isAtLimit
              ? 'text-red-600 dark:text-red-400'
              : isNearLimit
                ? 'text-orange-600 dark:text-orange-400'
                : 'text-primary'
          )}>
            {isLoadingUsage ? '...' : `${applicationsUsed}/${FREE_APPLICATION_LIMIT}`}
          </span>
        </div>
      )}

      {/* Upgrade button */}
      <button
        onClick={handleUpgradeClick}
        className={cn(
          'flex items-center justify-center gap-2 px-4 py-2.5 rounded-full transition-all',
          'bg-gradient-to-r from-primary to-cyan-600 hover:from-primary/90 hover:to-cyan-600/90',
          'text-white text-sm font-semibold shadow-md hover:shadow-lg'
        )}
      >
        <Crown className="h-4 w-4" />
        <span>Upgrade</span>
        <ArrowUpRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export const TierBadge = React.memo(TierBadgeComponent);
